import { useState, useEffect } from 'react'
import { Link as RouterLink, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Alert from '@mui/material/Alert'
import { Mail } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { loginPageStyles } from './LoginPage.styles'

const RESEND_COOLDOWN = 60

function EmailVerificationPage() {
    const { t } = useTranslation()
    const { user, isAuthenticated, emailVerified, sendVerificationEmail, reloadUser } = useAuth()
    const [cooldown, setCooldown] = useState(0)
    const [resendSuccess, setResendSuccess] = useState(false)
    const [resendError, setResendError] = useState('')
    const [checking, setChecking] = useState(false)
    const [notVerifiedYet, setNotVerifiedYet] = useState(false)

    useEffect(() => {
        if (cooldown <= 0) return
        const timer = setTimeout(() => setCooldown((c) => c - 1), 1000)
        return () => clearTimeout(timer)
    }, [cooldown])

    if (!isAuthenticated) return <Navigate to="/login" replace />
    if (emailVerified) return <Navigate to="/app" replace />

    const handleResend = async () => {
        setResendError('')
        setResendSuccess(false)
        try {
            await sendVerificationEmail()
            setResendSuccess(true)
            setCooldown(RESEND_COOLDOWN)
        } catch {
            setResendError(t('auth.verifyEmail.failed'))
        }
    }

    const handleCheck = async () => {
        setNotVerifiedYet(false)
        setChecking(true)
        try {
            await reloadUser()
            // emailVerified will be re-evaluated — if true, Navigate above will redirect
            setNotVerifiedYet(true)
        } finally {
            setChecking(false)
        }
    }

    return (
        <Container maxWidth="xs" sx={loginPageStyles.container}>
            <Box sx={loginPageStyles.logoWrapper}>
                <Box sx={{ ...loginPageStyles.logoIcon, bgcolor: 'primary.main' }}>
                    <Mail size={28} color="white" />
                </Box>
                <Typography variant="h5" fontWeight={700} textAlign="center">
                    {t('auth.verifyEmail.title')}
                </Typography>
            </Box>

            <Paper elevation={0} sx={loginPageStyles.paper}>
                {resendError && (
                    <Alert severity="error" sx={loginPageStyles.errorAlert}>{resendError}</Alert>
                )}
                {resendSuccess && (
                    <Alert severity="success" sx={loginPageStyles.errorAlert}>{t('auth.verifyEmail.resendSuccess')}</Alert>
                )}
                {notVerifiedYet && (
                    <Alert severity="warning" sx={loginPageStyles.errorAlert}>{t('auth.verifyEmail.notYet')}</Alert>
                )}

                <Typography variant="body2" color="text.secondary" mb={3}>
                    {t('auth.verifyEmail.description', { email: user?.email ?? '' })}
                </Typography>

                <Box sx={loginPageStyles.form}>
                    <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleCheck}
                        disabled={checking}
                    >
                        {t('auth.verifyEmail.check')}
                    </Button>

                    <Button
                        fullWidth
                        variant="outlined"
                        size="large"
                        onClick={handleResend}
                        disabled={cooldown > 0}
                    >
                        {cooldown > 0
                            ? t('auth.verifyEmail.resendCooldown', { seconds: cooldown })
                            : t('auth.verifyEmail.resend')}
                    </Button>
                </Box>

                <Typography variant="body2" color="text.secondary" textAlign="center" mt={2}>
                    <Link component={RouterLink} to="/login" fontWeight={600}>
                        {t('auth.backToLogin')}
                    </Link>
                </Typography>
            </Paper>
        </Container>
    )
}

export default EmailVerificationPage
